import React, { useState, useEffect } from 'react';
import { Manga, MangaSource, TierGrade, TIER_CONFIGS, KnownSite, TIER_ORDER } from '../types/manga';
import { getSourceName } from '../utils/helpers';
import { autoDiscoverMangaOnSite, checkSourceChapter, searchUpManga } from '../services/scraper';
import { X, Plus, Trash2, Link as LinkIcon, Image as ImageIcon, Upload, Search } from 'lucide-react';
import { showConfirm, showAlert } from './ConfirmDialog';

interface MangaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (manga: Manga) => void;
  onDelete?: (id: string) => void;
  initialData?: Manga | null;
  knownSites?: KnownSite[];
}

export const MangaModal: React.FC<MangaModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onDelete,
  initialData,
  knownSites = [],
}) => {
  const [title, setTitle] = useState('');
  const [grade, setGrade] = useState<TierGrade>('EX');
  const [status, setStatus] = useState('ยังไม่จบ');
  const [customPhase, setCustomPhase] = useState('4');
  const [nationality, setNationality] = useState('');
  const [readCh, setReadCh] = useState(0);
  const [currentCh, setCurrentCh] = useState(0);
  const [imageUrl, setImageUrl] = useState('');
  const [sources, setSources] = useState<MangaSource[]>([]);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isAutoSearching, setIsAutoSearching] = useState(false);
  const [isFetchingCover, setIsFetchingCover] = useState(false);
  const [searchLog, setSearchLog] = useState<string | null>(null);

  // Prevent background scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  useEffect(() => {
    if (initialData) {
      setTitle(initialData.title || '');
      setGrade(initialData.grade || 'C');
      setNationality(initialData.nationality || '');
      setReadCh(Number(initialData.readCh) || 0);
      setCurrentCh(Number(initialData.currentCh) || 0);
      setImageUrl(initialData.imageUrl || '');
      setImagePreview(initialData.imageUrl || null);
      setSearchLog(null);

      // Status
      const st = initialData.status || 'ยังไม่จบ';
      if (['ยังไม่จบ', 'จบภาค 1', 'จบภาค 2', 'จบภาค 3', 'จบแล้ว'].includes(st)) {
        setStatus(st);
      } else {
        const match = st.match(/^จบภาค\s*(\d+)$/);
        if (match) {
          setStatus('custom');
          setCustomPhase(match[1]);
        } else {
          setStatus('ยังไม่จบ');
        }
      }

      // Sources
      if (initialData.sources && initialData.sources.length > 0) {
        setSources(initialData.sources);
      } else if (initialData.sourceUrl) {
        setSources([
          {
            name: getSourceName(initialData.sourceUrl),
            url: initialData.sourceUrl,
            chapter: 0,
          },
        ]);
      } else {
        setSources([{ name: '', url: '', chapter: 0 }]);
      }
    } else {
      // Reset for new manga
      setTitle('');
      setGrade('EX');
      setStatus('ยังไม่จบ');
      setCustomPhase('4');
      setNationality('');
      setReadCh(0);
      setCurrentCh(0);
      setImageUrl('');
      setImagePreview(null);
      setSearchLog(null);
      setSources([{ name: '', url: '', chapter: 0 }]);
    }
  }, [initialData, isOpen]);

  if (!isOpen) return null;

  const handleAddSource = () => {
    setSources([...sources, { name: '', url: '', chapter: 0 }]);
  };

  const handleRemoveSource = (index: number) => {
    setSources(sources.filter((_, i) => i !== index));
  };

  const handleSourceChange = (index: number, field: keyof MangaSource, value: string | number) => {
    const updated = [...sources];
    if (field === 'url') {
      const urlStr = String(value);
      updated[index].url = urlStr;
      if (!updated[index].name && urlStr) {
        updated[index].name = getSourceName(urlStr);
      }
    } else {
      // @ts-ignore
      updated[index][field] = value;
    }
    setSources(updated);
  };

  // Auto-discover sources from Known Sites
  const handleAutoDiscoverSources = async () => {
    if (!title.trim()) {
      showAlert('กรุณากรอกชื่อเรื่องก่อน', 'ต้องมีชื่อเรื่องก่อนจึงจะค้นหาลิงก์อัตโนมัติได้');
      return;
    }

    setIsAutoSearching(true);
    setSearchLog('กำลังค้นหาลิงก์จากเว็บต่างๆ...');

    try {
      const newSources = [...sources.filter((s) => s.url.trim() !== '')];
      let discoveredCount = 0;
      let highestFoundCh = currentCh;

      const missingSites = knownSites.filter((site) => {
        const siteHost = getSourceName(site.url).toLowerCase();
        return !newSources.some((s) => getSourceName(s.url).toLowerCase() === siteHost);
      });

      if (missingSites.length === 0) {
        setSearchLog('เรื่องนี้มีลิงก์ครบทุกเว็บแล้ว');
        setIsAutoSearching(false);
        return;
      }

      setSearchLog(`กำลังค้นหาใน ${missingSites.map((s) => s.name).join(', ')}...`);

      const results = await Promise.all(
        missingSites.map(async (site) => {
          try {
            const found = await autoDiscoverMangaOnSite(site, title.trim());
            if (found && found.url) {
              let foundCh = found.chapter || 0;
              if (foundCh === 0) {
                const check = await checkSourceChapter(
                  { name: site.name, url: found.url, chapter: 0 },
                  2500,
                  title.trim()
                );
                if (!check.isMismatched) {
                  foundCh = check.latestChapter || 0;
                }
              }
              return { site, url: found.url, chapter: foundCh, coverUrl: found.coverUrl };
            }
          } catch {}
          return null;
        })
      );

      let autoCoverFound = '';
      for (const res of results) {
        if (res) {
          newSources.push({
            name: res.site.name,
            url: res.url,
            chapter: res.chapter,
          });
          if (res.chapter > highestFoundCh) {
            highestFoundCh = res.chapter;
          }
          if (res.coverUrl && !autoCoverFound) {
            autoCoverFound = res.coverUrl;
          }
          discoveredCount++;
        }
      }

      setSources(newSources.length > 0 ? newSources : [{ name: '', url: '', chapter: 0 }]);
      if (highestFoundCh > currentCh) {
        setCurrentCh(highestFoundCh);
      }
      if (autoCoverFound && (!imageUrl || imageUrl.trim() === '')) {
        setImageUrl(autoCoverFound);
        setImagePreview(autoCoverFound);
      }

      if (discoveredCount > 0) {
        setSearchLog(`✓ ค้นพบและเพิ่มลิงก์ใหม่ ${discoveredCount} เว็บสำเร็จ! (ตอนล่าสุด: ${highestFoundCh})`);
      } else {
        setSearchLog('ไม่พบลิงก์ใหม่เพิ่มเติมจากเว็บที่ตั้งค่าไว้');
      }
    } catch (err: any) {
      setSearchLog(`เกิดข้อผิดพลาด: ${err.message}`);
    } finally {
      setIsAutoSearching(false);
    }
  };

  const handleImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        const rawResult = reader.result as string;
        const img = new Image();
        img.onload = () => {
          // Sharp HD cover resolution (600x800 Aspect 3:4 for Retina & High-DPI screens)
          const maxW = 600;
          const maxH = 800;
          let w = img.width;
          let h = img.height;
          if (w > maxW || h > maxH) {
            const ratio = Math.min(maxW / w, maxH / h);
            w = Math.round(w * ratio);
            h = Math.round(h * ratio);
          }
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            ctx.drawImage(img, 0, 0, w, h);
            let compressed = canvas.toDataURL('image/webp', 0.88);
            if (!compressed.startsWith('data:image/webp')) {
              compressed = canvas.toDataURL('image/jpeg', 0.88);
            }
            setImageUrl(compressed);
            setImagePreview(compressed);
            return;
          }
          setImageUrl(rawResult);
          setImagePreview(rawResult);
        };
        img.onerror = () => {
          setImageUrl(rawResult);
          setImagePreview(rawResult);
        };
        img.src = rawResult;
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const userCurrentCh = Number(currentCh) || 0;
    const userReadCh = Number(readCh) || 0;

    const finalStatus = status === 'custom' ? `จบภาค ${customPhase || '4'}` : status;
    const cleanedSources = sources
      .filter((s) => s.url.trim() !== '')
      .map((s, idx) => {
        let ch = Number(s.chapter) || 0;
        // Keep primary source chapter in sync with manual user chapter input
        if (idx === 0 && userCurrentCh > ch) {
          ch = userCurrentCh;
        }
        return {
          name: s.name.trim() || getSourceName(s.url),
          url: s.url.trim(),
          chapter: ch,
        };
      });

    const latestSource = cleanedSources.reduce<MangaSource | null>((prev, curr) => {
      if (!prev || curr.chapter > prev.chapter) return curr;
      return prev;
    }, null);

    const mangaData: Manga = {
      id: initialData?.id || `ID_${Date.now()}`,
      title: title.trim(),
      grade,
      status: finalStatus,
      nationality,
      currentCh: userCurrentCh,
      readCh: userReadCh,
      imageUrl: imageUrl.trim(),
      sources: cleanedSources,
      latestSourceUrl: latestSource?.url || initialData?.latestSourceUrl || '',
      latestSourceName: latestSource?.name || initialData?.latestSourceName || '',
      order: initialData?.order ?? 999999,
      lastUpdatedAt: initialData?.lastUpdatedAt || '',
    };

    onSave(mangaData);
    onClose();
  };

  return (
    <div className="modal-overlay safe-area-modal">
      <div onClick={(e) => e.stopPropagation()} className="modal-panel max-w-xl">
        {/* Fixed Header */}
        <div className="modal-header">
          <h3 className="modal-title">{initialData ? 'แก้ไขข้อมูลมังงะ' : 'เพิ่มมังงะเรื่องใหม่'}</h3>
          <button onClick={onClose} type="button" title="ปิด" className="icon-btn -mr-2">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable Form Body with native smooth scrolling */}
        <form
          id="manga-edit-form"
          onSubmit={handleSubmit}
          className="modal-body space-y-5"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {/* Title */}
          <div>
            <label className="field-label">
              ชื่อเรื่องมังงะ <span className="text-rose-400">*</span>
            </label>
            <input
              type="text"
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. My Simulated Path to Immortality"
              className="field"
            />
          </div>

          {/* Tier Selection */}
          <div>
            <label className="field-label">ระดับเกรด (Tier)</label>
            <div className="grid grid-cols-4 sm:grid-cols-8 gap-1.5">
              {TIER_ORDER.map((t) => {
                const cfg = TIER_CONFIGS[t];
                const isSelected = grade === t;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setGrade(t)}
                    title={cfg.subtitle}
                    className={`h-9 rounded-lg text-sm font-semibold transition-colors ${
                      isSelected
                        ? cfg.solid
                        : 'bg-white/[0.04] text-zinc-400 ring-1 ring-inset ring-white/[0.06] hover:text-zinc-100'
                    }`}
                  >
                    {t}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Nationality & Status */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="field-label">สัญชาติมังงะ</label>
              <select value={nationality} onChange={(e) => setNationality(e.target.value)} className="field">
                <option value="">ไม่ระบุ</option>
                <option value="🇰🇷">🇰🇷 เกาหลี (Manhwa)</option>
                <option value="🇨🇳">🇨🇳 จีน (Manhua)</option>
                <option value="🇯🇵">🇯🇵 ญี่ปุ่น (Manga)</option>
              </select>
            </div>

            <div>
              <label className="field-label">สถานะเรื่อง</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className="field">
                <option value="ยังไม่จบ">ยังไม่จบ</option>
                <option value="จบภาค 1">จบภาค 1</option>
                <option value="จบภาค 2">จบภาค 2</option>
                <option value="จบภาค 3">จบภาค 3</option>
                <option value="custom">จบภาค... (ระบุตัวเลข)</option>
                <option value="จบแล้ว">จบแล้ว</option>
              </select>
              {status === 'custom' && (
                <input
                  type="number"
                  min="4"
                  value={customPhase}
                  onChange={(e) => setCustomPhase(e.target.value)}
                  placeholder="ระบุภาคที่ เช่น 4"
                  className="field mt-2"
                />
              )}
            </div>
          </div>

          {/* Chapters (Read & Current) */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="field-label">ตอนที่อ่านไปแล้ว</label>
              <input
                type="number"
                min="0"
                step="any"
                required
                value={readCh}
                onChange={(e) => setReadCh(parseFloat(e.target.value) || 0)}
                className="field tabular-nums"
              />
            </div>

            <div>
              <label className="field-label">ตอนปัจจุบัน (ล่าสุด)</label>
              <input
                type="number"
                min="0"
                step="any"
                required
                value={currentCh}
                onChange={(e) => setCurrentCh(parseFloat(e.target.value) || 0)}
                className="field tabular-nums"
              />
            </div>
          </div>

          {/* Sources List with Auto-Discover Button */}
          <div className="border-t border-white/[0.06] pt-5">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2.5">
              <label className="text-sm font-semibold text-zinc-100 flex items-center gap-2">
                <LinkIcon className="w-4 h-4 text-zinc-500" />
                <span>แหล่งข้อมูล</span>
                <span className="text-xs font-normal text-zinc-500">เช็คตอนจากหลายเว็บ</span>
              </label>

              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={handleAutoDiscoverSources}
                  disabled={isAutoSearching || !title.trim()}
                  title="ค้นหาเรื่องนี้จากเว็บที่ตั้งค่าไว้ให้อัตโนมัติ"
                  className="btn btn-sm btn-secondary"
                >
                  <Search className={`w-3.5 h-3.5 ${isAutoSearching ? 'animate-spin' : ''}`} />
                  <span>{isAutoSearching ? 'กำลังหา...' : 'ค้นหาอัตโนมัติ'}</span>
                </button>

                <button type="button" onClick={handleAddSource} className="btn btn-sm btn-secondary">
                  <Plus className="w-3.5 h-3.5" />
                  <span>เพิ่มเว็บ</span>
                </button>
              </div>
            </div>

            {searchLog && (
              <p className="text-[11px] text-zinc-400 mb-2.5 px-3 py-2 rounded-lg bg-white/[0.03] ring-1 ring-inset ring-white/[0.06]">
                {searchLog}
              </p>
            )}

            <div className="space-y-2">
              {sources.map((src, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={src.name}
                    placeholder="ชื่อเว็บ"
                    onChange={(e) => handleSourceChange(idx, 'name', e.target.value)}
                    className="field w-1/3 text-xs sm:text-sm"
                  />
                  <input
                    type="url"
                    value={src.url}
                    placeholder="https://..."
                    onChange={(e) => handleSourceChange(idx, 'url', e.target.value)}
                    className="field flex-1 w-auto text-xs sm:text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveSource(idx)}
                    className="icon-btn hover:text-rose-400 hover:bg-rose-500/10"
                    title="ลบแหล่งนี้"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Cover Image */}
          <div className="border-t border-white/[0.06] pt-5">
            <label className="text-sm font-semibold text-zinc-100 mb-2.5 flex items-center gap-2">
              <ImageIcon className="w-4 h-4 text-zinc-500" />
              <span>รูปภาพหน้าปก</span>
            </label>

            <div className="flex gap-3 items-start">
              {imagePreview && (
                <div className="w-16 aspect-[3/4] rounded-lg overflow-hidden bg-zinc-900 ring-1 ring-inset ring-white/[0.06] shrink-0">
                  <img src={imagePreview} alt="Preview" referrerPolicy="no-referrer" className="w-full h-full object-cover" />
                </div>
              )}
              <div className="flex-1 min-w-0 space-y-2">
                <input
                  type="text"
                  value={imageUrl}
                  onChange={(e) => {
                    setImageUrl(e.target.value);
                    setImagePreview(e.target.value || null);
                  }}
                  placeholder="URL รูปภาพ (https://... หรือ Google Drive link)"
                  className="field text-xs sm:text-sm"
                />

                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={async () => {
                      if (!title.trim()) {
                        showAlert('กรุณากรอกชื่อเรื่องก่อน', 'ต้องมีชื่อเรื่องก่อนจึงจะดึงรูปปกได้');
                        return;
                      }
                      setIsFetchingCover(true);
                      try {
                        const upFound = await searchUpManga(title.trim());
                        if (upFound && upFound.coverUrl) {
                          setImageUrl(upFound.coverUrl);
                          setImagePreview(upFound.coverUrl);
                        } else {
                          showAlert('ไม่พบรูปปกอัตโนมัติ', 'ใส่ URL รูปเองหรือกดอัปโหลดจากเครื่องแทน');
                        }
                      } catch {
                        showAlert('ดึงรูปภาพไม่สำเร็จ', 'ลองใหม่อีกครั้ง หรือใส่ URL รูปเอง');
                      } finally {
                        setIsFetchingCover(false);
                      }
                    }}
                    disabled={isFetchingCover || !title.trim()}
                    className="btn btn-sm btn-secondary"
                  >
                    <Search className={`w-3.5 h-3.5 ${isFetchingCover ? 'animate-spin' : ''}`} />
                    <span>{isFetchingCover ? 'กำลังดึงรูป...' : 'ดึงรูปจากเว็บ'}</span>
                  </button>

                  <label className="btn btn-sm btn-secondary cursor-pointer">
                    <Upload className="w-3.5 h-3.5" />
                    <span>อัปโหลดจากเครื่อง</span>
                    <input type="file" accept="image/*" onChange={handleImageFileChange} className="hidden" />
                  </label>
                </div>
                <p className="text-[11px] text-zinc-500">รองรับ Web URL, Drive หรือไฟล์ภาพ HD</p>
              </div>
            </div>
          </div>
        </form>

        {/* Fixed Footer at bottom */}
        <div className="modal-footer justify-between">
          {initialData && onDelete ? (
            <button
              type="button"
              onClick={async () => {
                if (
                  await showConfirm({
                    title: 'ลบเรื่องนี้ออกจากรายการ?',
                    message: `"${title}" จะถูกลบออกจากแรงค์ของคุณ`,
                    confirmLabel: 'ลบเรื่องนี้',
                    danger: true,
                  })
                ) {
                  onDelete(initialData.id);
                  onClose();
                }
              }}
              className="btn btn-danger -ml-2 px-2.5"
            >
              <Trash2 className="w-4 h-4" />
              <span>ลบเรื่องนี้</span>
            </button>
          ) : (
            <div />
          )}

          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} className="btn btn-ghost">
              ยกเลิก
            </button>
            <button type="submit" form="manga-edit-form" className="btn btn-primary px-5">
              บันทึก
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
